import { useEffect, useState } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
} from "@chakra-ui/react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { PortableText, type PortableTextComponents } from "@portabletext/react";
import PublicNavbar from "../Landing/PublicNavbar";
import PublicFooter from "../Landing/PublicFooter";
import { fetchBlogPostBySlug } from "../../../services/strapi/queries";
import { urlForImage } from "../../../services/strapi/client";
import type { BlogPost } from "../../../services/strapi/types";
import { WashiTape, DoodlePaperclip } from "../../CustomUi/DoodleIcons";

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString("en-NG", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}

// Tailored Portable Text rendering — Chakra-friendly typography that
// matches the rest of the site.
const ptComponents: PortableTextComponents = {
    block: {
        normal: ({ children }) => (
            <Text fontSize="md" color="gray.700" lineHeight="1.85" mb={4}>{children}</Text>
        ),
        h2: ({ children }) => (
            <Heading as="h2" fontSize="2xl" color="gray.900" mt={10} mb={4}>{children}</Heading>
        ),
        h3: ({ children }) => (
            <Heading as="h3" fontSize="xl" color="gray.900" mt={8} mb={3}>{children}</Heading>
        ),
        blockquote: ({ children }) => (
            <Box borderLeftWidth="3px" borderColor="brand.800" pl={4} my={6} fontStyle="italic" color="gray.600">{children}</Box>
        ),
    },
    types: {
        image: ({ value }) => {
            const url = urlForImage(value).width(1200).url();
            return (
                <Box my={6} borderRadius="lg" overflow="hidden">
                    <img src={url} alt="" style={{ width: "100%", display: "block" }} />
                </Box>
            );
        },
    },
    marks: {
        link: ({ children, value }) => (
            <a href={value?.href} target="_blank" rel="noopener noreferrer" style={{ color: "#0A3D2E", textDecoration: "underline" }}>
                {children}
            </a>
        ),
    },
};

const BlogPostPage = () => {
    const { slug } = useParams<{ slug: string }>();
    const [post, setPost] = useState<BlogPost | null | "loading" | "missing">("loading");

    useEffect(() => {
        if (!slug) return;
        fetchBlogPostBySlug(slug).then((p) => {
            const found = p ?? "missing";
            setPost(found);
            if (p && typeof p === "object" && p.title) {
                document.title = `${p.title} | Result Notebook`;
            } else {
                document.title = "Post Not Found | Result Notebook";
            }
        });
    }, [slug]);

    if (post === "loading") {
        return (
            <Box minH="100vh" bg="white">
                <PublicNavbar />
                <Container maxW="3xl" py={24}>
                    <Text textAlign="center" color="gray.500">Loading…</Text>
                </Container>
                <PublicFooter />
            </Box>
        );
    }

    if (post === "missing" || post === null) {
        return (
            <Box minH="100vh" bg="white">
                <PublicNavbar />
                <Container maxW="3xl" py={24}>
                    <VStack gap={4} textAlign="center">
                        <Heading as="h1" fontSize="2xl" color="gray.900">Post not found</Heading>
                        <Text color="gray.500">This post may have been moved or unpublished.</Text>
                        <RouterLink to="/blog">
                            <Text color="brand.800" fontWeight="medium" mt={2}>← Back to blog</Text>
                        </RouterLink>
                    </VStack>
                </Container>
                <PublicFooter />
            </Box>
        );
    }

    const cover = post.coverImage
        ? urlForImage(post.coverImage).width(1600).height(900).url()
        : null;

    return (
        <Box minH="100vh" bg="#FAF8F2">
            <PublicNavbar />

            <Box py={[12, 16]} className="notebook-dots">
                <Container maxW="3xl">
                    <Box
                        position="relative"
                        p={[6, 10]}
                        bg="white"
                        borderRadius="2xl"
                        border="3px solid #0A3D2E"
                        boxShadow="6px 8px 0px #0A3D2E"
                    >
                        <WashiTape angle={-3} top="-14px" left="32px" tapeColor="#FEF08A" label="Article 📖" />
                        <DoodlePaperclip color="#10B981" size={36} position="absolute" top="-16px" right="36px" />

                        <VStack align="start" gap={6} pt={2}>
                            <RouterLink to="/blog">
                                <Text color="brand.800" fontWeight="bold" fontSize="sm" _hover={{ textDecoration: "underline" }}>
                                    ← Back to notes
                                </Text>
                            </RouterLink>

                            <HStack gap={2} fontSize="xs" color="gray.500" fontWeight="bold">
                                <Text>{formatDate(post.publishedAt)}</Text>
                                {post.author?.name && (
                                    <>
                                        <Text>·</Text>
                                        <Text color="brand.800">{post.author.name}{post.author.role ? `, ${post.author.role}` : ""}</Text>
                                    </>
                                )}
                            </HStack>

                            <Heading as="h1" fontSize={["2xl", "3xl", "4xl"]} fontWeight="extrabold" color="gray.900" lineHeight="1.2">
                                {post.title}
                            </Heading>

                            {post.excerpt && (
                                <Text fontSize="md" color="gray.600" lineHeight="1.8" fontStyle="italic" bg="#FAF9F5" p={3} borderRadius="lg" borderLeft="3px solid #0A3D2E">
                                    {post.excerpt}
                                </Text>
                            )}

                            {cover && (
                                <Box w="full" aspectRatio={16 / 9} bg="gray.100" borderRadius="xl" overflow="hidden" mt={2} border="2px solid #0A3D2E">
                                    <img src={cover} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                                </Box>
                            )}

                            <Box w="full" mt={4} lineHeight="1.8">
                                {post.body ? (
                                    <PortableText value={post.body as never[]} components={ptComponents} />
                                ) : (
                                    <Text color="gray.500">This post has no body content yet.</Text>
                                )}
                            </Box>
                        </VStack>
                    </Box>
                </Container>
            </Box>

            <PublicFooter />
        </Box>
    );
};

export default BlogPostPage;
